import { useCallback } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { trainingAPI } from "@api/TrainingAPI";
import { audioNotification } from "@components/Common/Notifications/audioNotification";
import { remapSpecQueryIds } from "../widgetIdRemap";
import { controllerBareId, remapControllerBindings } from "../../Processing/controllerBindings";
import { setDashboardOrder } from "../dashboardOrder";
import {
  fromServerPayload,
  type DashboardState,
  type DashboardAction,
  type WidgetEntry,
  type ServerDashboardPayload,
} from "../dashboardState";
import type { WidgetResponseSpec } from "../../Processing/types";
import type { DashboardListItem } from "../DashboardContext";

type ProcessAndStore = (widget: WidgetEntry, responseOverride?: WidgetResponseSpec | null) => void;

/**
 * Dashboard-level CRUD: rename / create / duplicate / delete / reorder / import.
 * All persistence goes through the shared `trainingAPI`; `importDashboard` reuses
 * the provider's `processAndStore` and marks data stale so the next auto-save
 * refetches.
 */
export function useDashboardCrud(opts: {
  state: DashboardState;
  dispatch: Dispatch<DashboardAction>;
  setDashboardList: Dispatch<SetStateAction<DashboardListItem[]>>;
  loadDashboard: (id: string) => Promise<void>;
  processAndStore: ProcessAndStore;
  dataStaleRef: MutableRefObject<boolean>;
}) {
  const { state, dispatch, setDashboardList, loadDashboard, processAndStore, dataStaleRef } = opts;

  const renameDashboard = useCallback(
    async (name: string) => {
      dispatch({ type: "SET_NAME", name });
      setDashboardList((prev) =>
        prev.map((d) =>
          d.dashboard_id === state.dashboardId ? { ...d, name } : d,
        ),
      );
      try {
        if (state.dashboardId) {
          await trainingAPI.patchDashboard(state.dashboardId, { name });
        }
      } catch (err) {
        audioNotification.error({
          message: "Rename failed",
          description: "Failed to rename dashboard. Please try again.",
          placement: "top",
          duration: 5,
        });
      }
    },
    [state.dashboardId],
  );

  const createDashboard = useCallback(async () => {
    try {
      const newId = crypto.randomUUID();
      const name = "Unnamed";
      await trainingAPI.createDashboard({ name, info: { id: newId } });
      setDashboardList((prev) => [
        ...prev,
        { dashboard_id: newId, name, created_at: new Date().toISOString() },
      ]);
      await loadDashboard(newId);
    } catch (err) {
      audioNotification.error({
        message: "Create failed",
        description: "Failed to create dashboard. Please try again.",
        placement: "top",
        duration: 5,
      });
    }
  }, [loadDashboard]);

  const duplicateDashboard = useCallback(async () => {
    if (!state.dashboardId) return;
    try {
      const result = await trainingAPI.duplicateDashboard(state.dashboardId);
      const newId = String(result.id);
      setDashboardList((prev) => [
        ...prev,
        { dashboard_id: newId, name: result.name, created_at: result.created_at },
      ]);
      await loadDashboard(newId);
    } catch (err) {
      audioNotification.error({
        message: "Duplicate failed",
        description: "Failed to duplicate dashboard. Please try again.",
        placement: "top",
        duration: 5,
      });
    }
  }, [state.dashboardId, loadDashboard]);

  const deleteDashboard = useCallback(async () => {
    if (!state.dashboardId) return;
    try {
      const deletedId = state.dashboardId;
      await trainingAPI.deleteDashboard(deletedId);
      setDashboardList((prev) => {
        const remaining = prev.filter((d) => d.dashboard_id !== deletedId);
        if (remaining.length > 0) {
          loadDashboard(remaining[0].dashboard_id);
        } else {
          createDashboard();
        }
        return remaining;
      });
    } catch (err) {
      audioNotification.error({
        message: "Delete failed",
        description: "Failed to delete dashboard. Please try again.",
        placement: "top",
        duration: 5,
      });
    }
  }, [state.dashboardId, loadDashboard, createDashboard]);

  const reorderDashboards = useCallback((orderedIds: string[]) => {
    setDashboardOrder(orderedIds);
    setDashboardList((prev) => {
      const byId = new Map(prev.map((d) => [d.dashboard_id, d]));
      const next = orderedIds.map((id) => byId.get(id)).filter(Boolean) as DashboardListItem[];
      // Safety: append any dashboards not covered by the incoming order.
      for (const d of prev) if (!byId.has(d.dashboard_id) || !orderedIds.includes(d.dashboard_id)) {
        if (!next.includes(d)) next.push(d);
      }
      return next;
    });
  }, []);

  const importDashboard = useCallback(
    (payload: ServerDashboardPayload) => {
      // Deep-clone so we can mutate freely
      const clone: ServerDashboardPayload = JSON.parse(JSON.stringify(payload));

      // 1. Remap dashboard ID to the currently open dashboard
      const targetDashId = state.dashboardId ?? crypto.randomUUID();
      clone.id = targetDashId;

      // 2. Build a UUID remap table for widget IDs
      const widgetIdMap = new Map<string, string>();
      for (const w of clone.widgets) {
        const typePart = w.type;
        const newId = `${typePart}-${crypto.randomUUID()}`;
        widgetIdMap.set(w.id, newId);
        w.id = newId;
      }

      // 3. Remap layout entries to match new widget IDs
      for (const item of clone.layout) {
        item.i = widgetIdMap.get(item.i) ?? item.i;
      }

      // 4. Remap UUIDs inside widget specs (series queryIds + cross-refs)
      for (const w of clone.widgets) {
        if (w.spec) remapSpecQueryIds(w.spec);
      }

      // 5. Point every slicer binding at the imported copy of its controller.
      //    Builder misc stores the bare UUID, so the map is keyed on bare ids.
      const bareIdMap = new Map<string, string>();
      for (const [oldId, newId] of widgetIdMap) {
        bareIdMap.set(controllerBareId(oldId), controllerBareId(newId));
      }
      for (const w of clone.widgets) {
        remapControllerBindings(w.spec, bareIdMap);
      }

      const parsed = fromServerPayload(clone);
      dispatch({
        type: "LOAD_SUCCESS",
        dashboardId: targetDashId,
        name: parsed.name,
        widgets: parsed.widgets,
        layout: parsed.layout,
      });
      for (const widget of Object.values(parsed.widgets)) {
        processAndStore(widget);
      }
      dataStaleRef.current = true;
    },
    [state.dashboardId, processAndStore],
  );

  return {
    renameDashboard,
    createDashboard,
    duplicateDashboard,
    deleteDashboard,
    reorderDashboards,
    importDashboard,
  };
}
